<template>
  <div id="app">
    <!-- 1590879600000 timestamp (see below) = 31st May 2020 -->
    <v-app
      v-if="!user || user.termsAgreed || new Date(user.accountCreated).getTime() > 1590879600000"
      :style="bodyColors"
    >
      <ToolbarTop />
      <v-main>
        <router-view />
      </v-main>
      <v-snackbar
        v-model="snackbarVisible"
        :color="snackbar.color"
        class="text-white"
      >
        {{ snackbar.content }}
      </v-snackbar>
      <!-- <v-btn style="position: fixed; bottom: 30px; left: 5vh;" @click="$store.commit('toggleTooltips')">Toggle tooltips</v-btn> -->
    </v-app>
    <v-app v-else>
      <v-main class="onyx text-leaf">
        <v-container class="pt-10">
          <v-row>
            <v-col>
              <div class="text-h4 mb-3">
                Terms of service update
              </div>
              <div class="text-h6 mb-5">
                We've made some changes to our terms that you'll need to read and agree to before continuing:
              </div>
              <v-divider />

              <v-card
                style="height: calc(100vh - 270px); overflow-y: auto;"
              >
                <!-- eslint-disable-next-line vue/no-v-html -- static markdown, no XSS risk -->
                <v-card-text
                  class="pt-2 terms-text"
                  v-html="termsText"
                />
              </v-card>

              <v-divider />

              <v-card-actions class="px-0 mt-2">
                <v-spacer />
                <!-- <v-btn
                  color="secondary"
                  variant="text"
                >
                  I do not agree
                </v-btn> -->
                <v-btn
                  color="white"
                  class="text-shamrock"
                  @click="agreeToTerms()"
                >
                  I have read and agree to the terms above
                </v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-container>
      </v-main>
    </v-app>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import ToolbarTop from '@/components/application/ToolbarTop'
import termsText from '@/assets/terms.md'

export default {
  name: 'App',
  components: {
    ToolbarTop
  },
  data () {
    return {
      termsText
    }
  },
  computed: {
    ...mapState(['snackbar', 'page']),
    user () {
      return this.$store.state.auth.user
    },
    snackbarVisible: {
      get () {
        return Boolean(this.snackbar.content)
      },
      set (value) {
        if (!value) {
          this.$store.commit('hideSnackbar')
        }
      }
    },
    bodyColors () {
      switch (this.page.content) {
        case 'dark':
          return { backgroundColor: 'rgb(var(--v-theme-onyx))', color: 'rgb(var(--v-theme-white))' }
        case 'light':
          return { backgroundColor: 'rgb(var(--v-theme-leaf))', color: 'rgb(var(--v-theme-primary))' }
        default:
          return { backgroundColor: '#fff', color: 'rgb(var(--v-theme-primary))' }
      }
    }
  },
  async created () {
    await this.$store.dispatch('fetchConfigs')
  },
  mounted () {
    this.setWinFromCurrentViewport()
    window.addEventListener('resize', this.setWinFromCurrentViewport)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.setWinFromCurrentViewport)
  },
  methods: {
    ...mapMutations(['setWin']),
    setWinFromCurrentViewport () {
      this.setWin({
        width: window.innerWidth,
        height: window.innerHeight
      })
    },
    agreeToTerms () {
      this.$store.dispatch('auth/agreeToTerms')
    }
  }
}
</script>

<style lang="scss">
@use "./styles/variables.scss";

/* @vueform/slider declares `sideEffects: false`, which lets webpack drop the
   bare `import '@vueform/slider/themes/default.css'` we'd otherwise do inside
   the consuming component (and even in main.ts). Pulling the CSS in via SCSS
   `@import url(...)` here makes the browser fetch it as a stylesheet at
   runtime, bypassing webpack's tree-shake entirely. */
@import url('~@vueform/slider/themes/default.css');

html, body {
  font-family: 'Inter', 'Roboto', sans-serif;
  // overflow-y: auto !important;
  scroll-behavior: smooth;
  overscroll-behavior-x: none;
  // overflow-x: hidden !important;
  p {
    margin-bottom: 16px;
  }
  .v-btn {
    font-weight: 400;
    /* V2 set v-btn line-height to 18px; V3 leaves it `normal` (~14.4px at 12px font), tightening multi-line button labels and chip+icon vertical alignment. Restore V2. */
    line-height: 18px;
  }
  .v-btn--size-large {
    font-size: 14px;
    letter-spacing: 0.0892857143em;
  }
  .v-input__details {
    min-height: 14px;
    padding-top: 0;
    margin-top: 8px;
  }
  .v-field--variant-underlined .v-field__outline::before {
    opacity: 0.7;
  }
  /* V3 underlined v-input has --v-input-padding-top: 16px which puts the floating label far from the input. V2 was much tighter. Reduce so labels sit close to the field while still leaving room for the floating label and content. */
  .v-input.v-input--variant-underlined,
  .v-input:has(.v-field--variant-underlined) {
    --v-input-padding-top: 12px;
    --v-input-control-height: 40px;
  }
  /* V2 defined --v-anchor-base globally (= the primary theme color) and applied it to every <a> via `.v-application a { color: var(--v-anchor-base) }`. V3 dropped the variable, and our previous `a { color: inherit }` reset broke that pattern (anchors picked up whatever the parent text color happened to be). Re-introduce the variable and use it. */
  --v-anchor-base: rgb(var(--v-theme-primary));
  a {
    text-decoration: none;
    color: var(--v-anchor-base);
  }
  /* V3 v-toolbar-title doesn't set its own line-height anymore; let typography classes (text-h5 = 1.333) on the title govern. Removing the previous 1.5 override. */
  .text-h1, .text-h2, .text-h3, .text-h4, .text-h5, .text-h6,
  .text-subtitle-1, .text-subtitle-2,
  .text-body-1, .text-body-2,
  .text-button, .text-caption, .text-overline {
    font-family: 'Inter', 'Roboto', sans-serif !important;
  }
  /* V3 text-capitalize CSS only capitalizes words at detected word boundaries, and when the span is inline after other inline text (like an h1), the first letter's word boundary isn't detected. Forcing inline-block creates an isolated context. */
  .text-capitalize {
    display: inline-block;
  }
  /* Table cells must stay `display: table-cell` to participate in column layout -- the inline-block rule above breaks <th class="text-capitalize"> / <td class="text-capitalize"> usages (e.g. SiteConditionSection headers), causing columns to collapse, headers to wrap to new lines, and body cells to take arbitrary widths. */
  th.text-capitalize,
  td.text-capitalize {
    display: table-cell;
  }
  .v-icon--size-x-small  { font-size: 12px !important; }
  .v-icon--size-small    { font-size: 20px !important; }
  .v-icon--size-default  { font-size: 24px !important; }
  .v-icon--size-large    { font-size: 36px !important; }
  .v-icon--size-x-large  { font-size: 40px !important; }
  /* V2 icon-only buttons auto-sized their inner icons to match button size; V3 doesn't, leaving icons at the default 24px and overflowing small buttons. Re-derive icon size from the button's size class — only when the icon is using the default size (so explicit `<v-icon size="X">` still wins). */
  .v-btn--icon.v-btn--size-x-small  .v-icon.v-icon--size-default { font-size: 16px !important; }
  .v-btn--icon.v-btn--size-small    .v-icon.v-icon--size-default { font-size: 18px !important; }
  .v-btn--icon.v-btn--size-large    .v-icon.v-icon--size-default { font-size: 28px !important; }
  .v-btn--icon.v-btn--size-x-large  .v-icon.v-icon--size-default { font-size: 32px !important; }
  /* V3 v-btn defaults to text-transform: uppercase (Material). Project always wanted normal case. Letter-spacing keeps V2's default 0.0892857143em (~1.25px) for the spaced look. */
  .v-btn {
    text-transform: none !important;
  }
  /* V2 v-tab was uppercase, medium-weight, with letter spacing, and inactive tabs were 60% opacity. V3's v-tab inherits our v-btn overrides (which strip text-transform, letter-spacing, weight) so we re-apply tab styling here. */
  .v-tab {
    text-transform: uppercase !important;
    letter-spacing: 1.25px !important;
    font-weight: 500 !important;
  }
  .v-tab:not(.v-tab--selected):not(.v-tab--active) {
    /* Use opacity instead of a fixed theme-on-surface color so the dim works whether the tab sits on a light or dark background — V3 the slider/underline on inactive tabs is already opacity:0, so this doesn't hide it. */
    opacity: 0.6;
  }
  /* Vuetify 3's own reset (button { color: inherit }) ties specificity with v-btn's variant rules and wins by cascade order. Re-assert variant colors with higher specificity. */
  .v-btn.v-btn--variant-elevated,
  .v-btn.v-btn--variant-flat {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }
  /* Vuetify 2's <v-btn icon> had a transparent bg by default; V3 makes it filled (elevated). Reset to V2 behavior unless an explicit bg color class is applied. */
  .v-btn.v-btn--icon.v-btn--variant-elevated:not([class*="bg-"]) {
    background: transparent !important;
    box-shadow: none !important;
  }
  /* V2 default <v-btn> (no color prop) had a light-gray #f5f5f5 background; V3 changed to --v-theme-surface (white). Restore V2 default for unstyled elevated buttons (non-icon, no explicit color/bg class). */
  .v-btn.v-btn--variant-elevated:not(.v-btn--icon):not([class*="bg-"]):not([class*="text-"]) {
    background-color: #f5f5f5;
  }
  /* V3 v-card-actions has a Material spec min-height of 52px; V2 had 0. Restore V2 so action rows pack as tightly as their content. */
  .v-card-actions {
    min-height: 0;
  }
  /* V2 disabled buttons with a background painted rgba(0,0,0,0.12) bg + rgba(0,0,0,0.26) text (.theme--light.v-btn.v-btn--disabled.v-btn--has-bg). V3 keeps the button's color and stacks a translucent overlay on top, which doesn't read as "disabled" the way V2 did. Restore V2 visual: replace bg, dim text, and disable the overlay so it doesn't tint the bg. */
  .v-btn.v-btn--disabled.v-btn--variant-flat,
  .v-btn.v-btn--disabled.v-btn--variant-elevated,
  .v-btn.v-btn--disabled.v-btn--variant-tonal {
    background: rgba(0, 0, 0, 0.12) !important;
    background-color: rgba(0, 0, 0, 0.12) !important;
    color: rgba(0, 0, 0, 0.26) !important;
    opacity: 1 !important;
  }
  .v-btn.v-btn--disabled .v-btn__overlay,
  .v-btn.v-btn--disabled .v-btn__underlay {
    opacity: 0 !important;
    background: transparent !important;
  }
  /* V2 emphasis utility classes (text--primary/secondary/disabled, double-dash) were renamed in V3 to text-high-emphasis / text-medium-emphasis / text-disabled. Re-add the V2 names so existing markup keeps working. */
  .text--primary    { color: rgba(var(--v-theme-on-surface), 0.87) !important; }
  .text--secondary  { color: rgba(var(--v-theme-on-surface), 0.6) !important; }
  .text--disabled   { color: rgba(var(--v-theme-on-surface), 0.38) !important; }
  /* V3 v-app-bar adds a default elevation shadow even when content fits viewport. V2 was flat unless explicitly elevated. Match V2 by removing default shadow. */
  .v-app-bar {
    box-shadow: none !important;
  }
  /* V3 bumped v-label line-height (24px vs V2 20px) and added letter-spacing 0.15px, which makes form labels look taller and more spaced than V2. Restore V2 metrics. */
  .v-label {
    line-height: 20px;
    letter-spacing: normal;
  }
  /* V3 paints `.v-label` with `color: inherit; opacity: var(--v-medium-emphasis-opacity)`, so the resulting alpha depends on inherited color. Inside `.v-card > .v-card-text` (which we already pin to alpha 0.6 above) that becomes 0.6 × 0.6 = 0.36 — labels read washed-out. Outside cards it lands at full alpha × 0.6 = 0.6 (V2-correct). Pin the in-field label to V2's deterministic 0.6 alpha with no element opacity. Use the `--v-theme-on-surface` token (not literal black) so subtrees with `theme="dark"` (e.g. the New Project form) flip the label to white-on-dark — V2 did this via its `theme--dark` cascade. Scope to `.v-field-label` so checkbox/radio/switch labels keep their default behavior. V3's `.v-field--error … { color: rgb(var(--v-theme-error)) }` has higher specificity and still wins for error states. */
  .v-label.v-field-label {
    color: rgba(var(--v-theme-on-surface), 0.6);
    opacity: 1;
  }
  /* V3 v-expansion-panel-title min-height jumps to 64px when active (vs 48px collapsed); V2 stayed at 48px regardless. Lock to V2's compact height. */
  .v-expansion-panel-title,
  .v-expansion-panel-title--active {
    min-height: 48px;
  }
  /* V3 v-expansion-panel-title has a hover/focus overlay (`__overlay`) absolutely positioned across the header. The project's panels are click-to-toggle without a discrete hover affordance, so kill the overlay project-wide. */
  .v-expansion-panel-title__overlay {
    display: none;
  }
  /* V2 cascaded the disabled v-expansion-panels color (rgba(0,0,0,0.38)) all the way to header content so CategoryHeaders dimmed too. V3's color cascade stops at the header itself. Re-apply globally so any disabled panel matches V2. */
  .v-expansion-panel--disabled .v-expansion-panel-title,
  .v-expansion-panel--disabled .v-expansion-panel-title .category-header {
    color: rgb(0, 0, 0);
  }
  /* V2 hid the chevron when the panel was disabled (since the user can't toggle it). V3 keeps the chevron visible. Hide it to match. */
  .v-expansion-panel--disabled .v-expansion-panel-title__icon {
    display: none;
  }
  /* V3 introduced `.v-list-item__spacer` after `.v-list-item__prepend > .v-icon`, sized by `--v-list-prepend-gap` (default 32px). On top of the icon's ~24px width that pushed list-item content ~56px in. V2's equivalent `<v-list-item__icon class="mr-4">` only used 16px. Globally set the gap to 16px so every list-item (nav drawers, dropdowns, menus, action lists) tightens up to V2 spacing. Specific lists can still override the variable inline. */
  .v-list-item__prepend {
    --v-list-prepend-gap: 16px;
  }
  /* V3 sets `.v-list-item .v-icon { color: inherit }` (we re-add it above to fix tints in v-tabs, chips, etc.), which in nav rails makes the icon take the v-list-item's high-emphasis 0.87 color. V2's nav icons used the secondary-text 0.54. Bring nav-drawer prepend icons back to 0.54 unless an explicit text-* class is set. */
  .v-navigation-drawer .v-list-item__prepend .v-icon:not([class*="text-"]) {
    color: rgba(var(--v-theme-on-surface), 0.54);
  }
  /* V2 `<v-list dense>` shrunk v-list-item titles to 13px; V3 `density="compact"` only affects row min-height/padding, leaving the title at the v-list-item default 16px. Restore V2 behavior — when a v-list (or v-list-item) is in compact density, drop the title to 13px. */
  .v-list--density-compact .v-list-item-title,
  .v-list-item--density-compact .v-list-item-title {
    font-size: 13px;
  }
  /* V2's default `.v-dialog > .v-card > .v-card__subtitle, .v-card__text { padding: 0 24px 20px }` rule no longer fires because V3 renamed the BEM children: `__subtitle` → `-subtitle`, `__text` → `-text`. Re-add the rule with V3 selectors so dialog body content keeps the V2 horizontal/bottom padding. */
  .v-dialog > .v-card > .v-card-subtitle,
  .v-dialog > .v-card > .v-card-text {
    padding: 0 24px 20px;
  }
  /* V2 `.theme--light.v-card > .v-card__subtitle, .v-card__text { color: rgba(0,0,0,0.6) }` painted card-body text at 60% black for medium emphasis. V3 dropped the rule (V3 v-card-text/subtitle inherit color from the v-card, which sits at high-emphasis 0.87), so card paragraphs/subtitles look heavier than V2. Restore the medium-emphasis dim. */
  .v-card > .v-card-subtitle,
  .v-card > .v-card-text {
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
  /* V3 v-radio without a label still renders an empty <label class="v-label"/> after the wrapper, so its flex content is "wrapper + empty label" — a `justify-center` parent ends up centering wrapper-plus-empty-label, not the visible circle. The `fix-margin` class (used by RadioImage) marks radios that should hide the label and center the circle. */
  .v-radio.fix-margin {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  .v-radio.fix-margin .v-selection-control__wrapper {
    margin-inline: 0 !important;
    padding-inline: 0 !important;
  }
  .v-radio.fix-margin > .v-label,
  .v-radio.fix-margin .v-selection-control .v-label {
    display: none !important;
  }
  /* V3 v-tooltip rebased on v-overlay: lost V2's rgba(97,97,97,0.9) translucent bg — V3 ships opaque. Restore V2's semi-transparent dark gray. */
  .v-tooltip > .v-overlay__content {
    background: rgba(97, 97, 97, 0.9) !important;
    color: rgb(255, 255, 255);
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.4;
    padding: 5px 16px;
    pointer-events: none;
    /* CSS keyframe is the only way that's robust against V3's transition class name churn — fires whenever the overlay element mounts (which is every tooltip open since v-tooltip eagerly creates/destroys). Skips the leave transition, but the snap-out is fine; it's the entrance fade V2 had. */
    animation: cpd-tooltip-fade-in 0.25s ease-out;
  }
  /* Cancel any V3 transform-based transition (default `scale-transition`) on tooltip content so our keyframe owns the entrance. */
  .v-tooltip > .v-overlay__content[class*="-enter-active"],
  .v-tooltip > .v-overlay__content[class*="-leave-active"] {
    transition: opacity 0.25s ease-out !important;
    transform: none !important;
  }
  .v-tooltip > .v-overlay__content[class*="-enter-from"],
  .v-tooltip > .v-overlay__content[class*="-leave-to"] {
    opacity: 0;
    transform: none !important;
  }
  @keyframes cpd-tooltip-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  /* V3 v-parallax content area is display:block, so children sit at the top. V2's .v-parallax__content was flex-centered. Restore vertical centering. */
  .v-parallax .v-responsive__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  /* V3 v-banner__content is display:flex, which lays consecutive inline children side-by-side as flex columns. V2 was block, letting them flow inline. */
  .v-banner__content {
    display: block;
  }
  /* V2 v-icon defaulted to secondary-text color (54%/70% black/white). V3 dropped this and just inherits parent color, so default icons render at 87% emphasis. Restore V2 defaults; explicit text-* color classes use !important and still win. Icons inside interactive components (button, tab, chip, list-item) inherit from the parent so V2 patterns like <v-btn color="shamrock"><v-icon /></v-btn> still tint the icon. */
  .v-icon {
    color: rgba(var(--v-theme-on-surface), 0.54);
  }
  .v-btn .v-icon,
  .v-tab .v-icon,
  .v-chip .v-icon,
  .v-list-item .v-icon,
  /* v-radio / v-checkbox / v-switch all render their dot/check/thumb as a v-icon inside .v-selection-control. V3 colors them by applying `text-<theme>` to the .v-selection-control parent, which only works if the icon inherits color from that parent. Without this exemption the global `.v-icon { color: rgba(...,0.54) }` rule wins, so e.g. color="shamrock" on a v-radio never reaches the visible dot. */
  .v-selection-control .v-icon {
    color: inherit;
  }
  /* V2 icon-only buttons without an explicit color rendered their icons at 54% black (secondary text) — V3 inherits the button's high-emphasis 87% text color, so unstyled gear/delete icons look too dark. Restore V2 muted look only when no text-* color class is set. */
  .v-btn--icon:not([class*="text-"]) .v-icon {
    color: rgba(var(--v-theme-on-surface), 0.54);
  }
  /* V2 outlined buttons in the DISABLED state used a neutral 12%-black border;
     enabled outlined buttons inherited the color prop just like V3 does (border
     tracks currentColor / text color). The earlier unconditional rule applied
     the disabled-state neutral to every outlined button, which was correct for
     disabled but broke every enabled colored outlined button (TemplateList
     "New template", LayoutList "New dimension set", LayoutEditor "Edit",
     "Use material wizard", etc.) -- they all rendered with a faint gray border
     instead of V2's shamrock/colored hairline.
     Scope the override to disabled-only so enabled buttons keep V3's
     currentColor border (= V2 enabled behavior) and disabled buttons fall back
     to the neutral 12% (= V2 disabled behavior). */
  .v-btn--variant-outlined.v-btn--disabled {
    border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  }
  /* Same V2/V3 divergence as outlined buttons applies to outlined chips: V3's `border: thin solid currentColor` makes the chip outline track text color (defaults to high-emphasis 0.87 — visibly dark), whereas V2 painted a neutral `1px solid rgba(0,0,0,0.12)` regardless. Restore V2's neutral hairline. Affects the "editing" pill in TemplateForm header, every variable chip inside ImpactValues, and any other outlined chip site. */
  .v-chip--variant-outlined {
    border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  }
  /* V3 v-chip variant="flat" (the project's global VChip default in vuetify.js) defaults to the surface-variant theme token, which V3's built-in light theme paints as `#424242` (dark gray) with light on-surface-variant text. V2 default chips were `#e0e0e0` (light gray) with dark high-emphasis text — the inverse. Restore the V2 inversion for chips without an explicit color (`:not` selectors exempt anything that has a `bg-<color>` class or an inline `--v-chip-color` override). Affects autocomplete/combobox chip selections (Carbon Conscience IDs, Nursery Data, etc.) and any other default-color flat chip site. */
  .v-chip--variant-flat:not([class*="bg-"]):not([style*="--v-chip-color"]) {
    background-color: rgb(224, 224, 224);
    color: rgba(var(--v-theme-on-surface), 0.87);
  }
  /* V3 disables buttons via element-level opacity (0.26) and keeps the original color, which compounds with the outlined-border 12% alpha rule above to ~3% effective border alpha (almost invisible) and tints text as a faded version of the button's color (shamrock-at-26%) instead of V2's neutral gray. Restore V2's color-based disabled state: opacity:1, neutral 26% text, neutral 12% bg for filled variants. Specificity 0,2,0 beats utility .text-white (0,1,0) so disabled+text-white buttons (e.g. Save) also pick up the neutral gray. */
  .v-btn.v-btn--disabled {
    opacity: 1 !important;
    color: rgba(var(--v-theme-on-surface), 0.26) !important;
  }
  .v-btn--disabled.v-btn--variant-elevated,
  .v-btn--disabled.v-btn--variant-flat {
    background-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  }
  /* V3 v-text-field hides .v-text-field__suffix and __prefix (opacity: 0) until the field is .v-field--active (focused or filled) or .v-field--no-label, so unit suffixes on labeled empty fields (e.g. "kgCO₂e/kg" on the Create Nursery modal) are invisible. V2 always showed prefix/suffix regardless of state. Force visible. */
  .v-text-field__suffix,
  .v-text-field__prefix {
    opacity: 1 !important;
  }
  /* V3 v-data-table dropped V2's `.theme--light.v-data-table th { font-size: 12px; font-weight: 700; color: rgba(0,0,0,0.6) }` rule. V3 headers inherit body font (14px) at high-emphasis 0.87 — visibly bigger and lighter than V2. Restore V2 typography globally. `density="compact"` on the table only shrinks spacing, not header font, so a global rule is the only scaling fix. */
  .v-data-table thead th,
  .v-data-table-header__content {
    font-size: 12px;
    font-weight: 700;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
  /* V3 sets --v-table-header-height: 56px and --v-table-row-height: 52px on
     .v-table--density-default. V2's tables (admin lists with `dense`, in-modal
     <v-table> grids like the existing-site-conditions sections) rendered header
     AND body rows at 48 px. The V3 migration dropped `dense` (it became a no-op
     after the V2 -> V3 prop rename), so local rows landed at V3's default
     ~52-64 px instead of V2's tight 48 px. Force both to 48 px on every
     default-density table (covers <v-data-table> and plain <v-table>) --
     explicit density="comfortable"/"compact" tables keep their V3-prescribed
     heights. */
  .v-table--density-default {
    --v-table-header-height: 48px;
    --v-table-row-height: 48px;
  }
  .introjs-tooltiptext {
    font-family: "Inter", "Roboto", sans-serif !important;
  }
}
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  box-sizing: border-box;
  // background-color: rgb(var(--v-theme-leaf));
  // background: #fff;
  .theme--light.v-input input::placeholder, .theme--light.v-input textarea::placeholder {
    color: #888;
  }
  .v-card__title {
    word-break: normal !important;
  }
}
.cpd-tooltip {
    opacity: 0;
    transition: opacity 1s ease 0.25s;
    z-index: 3;
    position: absolute;
    pointer-events: none;
    background: rgba(97, 97, 97, 0.9);
    width: 300px;
    color: #fff;
    font-size: 13px;
    border-radius: 5px;
    padding: 7px 13px;
    &.right, &.left {
      top: 50%;
      transform: translateY(-50%);
      &.right {
        left: calc(100% + 10px);
      }
      &.left {
        right: calc(100% + 10px);
      }
    }
    &::after {
      content: "";
      position: absolute;
      right: 100%;
      top: 50%;
      transform: translateY(-50%);
      // draw arrow
      width: 0;
      height: 0;
      border-top: 7px solid transparent;
      border-bottom: 7px solid transparent;
      border-right: 7px solid rgba(97, 97, 97, 0.9);
    }
    &.show {
      opacity: 1;
    }
  }
</style>
